import { useState, useCallback, memo } from 'react';
import { useAdvancedSecurities } from '../../../../service/Hooks/Titres/useAdvancedSecurities';
import { getUserRole } from '../../../../service/api/AuthRoleService';
import SecurityAdvancedFilters from '../SecurityAdvancedFilters';
import SecurityRowAdvanced from '../SecurityRowAdvanced';
import SecurityPagination from '../SecurityPagination';
import SecurityDetailsModal from '../../Modal/SecurityDetailsModal';
import EditPriceModal from '../../Modal/EditPriceModal';
import SecurityHoldersModal from '../../Modal/SecurityHoldersModal';
import { FaExclamationTriangle, FaSyncAlt } from 'react-icons/fa';
import '../../style/titre.css';
import PrivateTransactionModal from '../../../../TransactionPrivee/PrivateTransactionModal';
import ReportGenerationModal from '../../../../TransactionPrivee/Reports/ReportGenerationModal';
import TransactionDetailsModal from '../../../../TransactionPrivee/Details/TransactionDetailsModal';
import useSelectedDomain from '../../../../service/Hooks/useSelectedDomain';
import PrivateTransactionsStatisticsModal from '../../Modal/PrivateTransactionsStatisticsModal';
import { deleteSecurityById, updateSecurityPrice } from '../../../../service/api/SecurityService';
import TableSkeleton from '../../../TableSkeleton';

const AdvancedSecurities = () => {
  const [filterLoading, setFilterLoading] = useState(false);
  const [selectedSecurityId, setSelectedSecurityId] = useState(null);
  const [selectedSecurityForHolders, setSelectedSecurityForHolders] = useState(null);
  const [selectedSecurityForReport, setSelectedSecurityForReport] = useState(null);
  const [selectedSecurityForStatistics, setSelectedSecurityForStatistics] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [showHoldersModal, setShowHoldersModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [editPriceData, setEditPriceData] = useState(null);
  const [reportMode, setReportMode] = useState('single');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedSecurityForTransaction, setSelectedSecurityForTransaction] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedSecurityForDocuments, setSelectedSecurityForDocuments] = useState(null);
  const [showTransactionDetailsModal, setShowTransactionDetailsModal] = useState(false);
  const [selectedSecurityForTransactions, setSelectedSecurityForTransactions] = useState(null);

  const selectedDomain = useSelectedDomain();

  const {
    securities,
    loading,
    isSearching,
    error,
    pagination,
    filters,
    setFilters,
    handleResetFilters,
    handlePageChange
  } = useAdvancedSecurities();

  const role = getUserRole();

  const showInitialLoading = loading && securities.length === 0 && !isSearching;
  const showFilterLoading = filterLoading || isSearching;
  const showNoResults = !loading && !filterLoading && securities.length === 0 && pagination.totalCount === 0;
  const showErrorState = error && error !== 'SearchCancelled' && !loading;

  const handleFilterChange = useCallback((newFilters) => {
    if (
      newFilters.securityName !== filters.securityName ||
      newFilters.securityType !== filters.securityType ||
      newFilters.targetMarket !== filters.targetMarket
    ) {
      setFilterLoading(true);
      setTimeout(() => setFilterLoading(false), 600);
    }
    setFilters(newFilters);
  }, [filters, setFilters]);

  const handleResetFiltersLocal = useCallback(() => {
    setFilterLoading(true);
    handleResetFilters();
    setTimeout(() => setFilterLoading(false), 400);
  }, [handleResetFilters]);

  // ── Handlers navigation ────────────────────────────────────────────────────
  const handleViewDetails = useCallback((securityId) => {
    setSelectedSecurityId(securityId);
    setShowDetailsModal(true);
  }, []);

  const handleEditPrice = useCallback((security) => {
    setEditPriceData({
      securityId: security.securityId,
      currentPrice: security.lastPrice,
      securityName: security.securityName || 'Titre inconnu'
    });
    setShowEditPriceModal(true);
  }, []);

  const handleViewHolders = useCallback((security) => {
    setSelectedSecurityForHolders({
      id: security.securityId,
      name: security.securityName,
      targetMarket: security.targetMarket
    });
    setShowHoldersModal(true);
  }, []);

  const handleGenerateReport = useCallback((security) => {
    setSelectedSecurityForReport({ id: security.securityId, name: security.securityName });
    setReportMode('single');
    setShowReportModal(true);
  }, []);

  const handleCreateTransaction = useCallback((security) => {
    setSelectedSecurityForTransaction(security);
    setShowTransactionModal(true);
  }, []);

  const handleViewStatistics = useCallback((security) => {
    setSelectedSecurityForStatistics({
      id: security.securityId,
      name: security.securityName,
      symbol: security.symbol
    });
    setShowStatisticsModal(true);
  }, []);

  const handleGenerateDocuments = useCallback((security) => {
    setSelectedSecurityForDocuments(security);
    setShowDocumentsModal(true);
  }, []);

  const handleViewTransactionDetails = useCallback((security) => {
    setSelectedSecurityForTransactions({
      id: security.securityId,
      name: security.securityName,
      domainId: selectedDomain.id
    });
    setShowTransactionDetailsModal(true);
  }, [selectedDomain]);

  // ── Rafraîchissement silencieux après une transaction créée ────────────────
  // Appelé par PrivateTransactionModal au succès — pas de toast, pas de spinner
  const handleTransactionSuccess = useCallback(() => {
    setShowTransactionModal(false);
    // Délai court pour laisser le modal se fermer proprement avant le fetch
    setTimeout(() => {
      handlePageChange(pagination.pageNumber);
    }, 300);
  }, [pagination.pageNumber, handlePageChange]);

  // ── Actions sur les titres ──────────────────────────────────────────────────
  const handleUpdatePrice = useCallback(async (updatedData) => {
    try {
      await updateSecurityPrice(updatedData);
      setShowEditPriceModal(false);
      handlePageChange(pagination.pageNumber);
      return true;
    } catch {
      return false;
    }
  }, [pagination.pageNumber, handlePageChange]);

  const handleDeleteSecurity = useCallback(async (securityId, securityName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${securityName}" ? Cette action est irréversible.`)) return;
    try {
      await deleteSecurityById(securityId);
      handlePageChange(pagination.pageNumber);
    } catch {
      // erreur silencieuse
    }
  }, [pagination.pageNumber, handlePageChange]);

  const handleRetry = useCallback(() => {
    handlePageChange(pagination.pageNumber);
  }, [pagination.pageNumber, handlePageChange]);

  // ── Rendu ──────────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (showInitialLoading) {
      return (
        <div className="py-5">
          <SecurityAdvancedFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFiltersLocal}
            loading={true}
            isSearching={false}
          />
          <TableSkeleton columns={9} rows={6} showHeader={true} />
        </div>
      );
    }

    if (showFilterLoading) {
      return (
        <div className="py-4">
          <SecurityAdvancedFilters
            filters={filters}
            onFilterChange={() => {}}
            onReset={handleResetFiltersLocal}
            loading={true}
            isSearching={false}
          />
          <TableSkeleton columns={10} rows={5} showHeader={true} />
        </div>
      );
    }

    if (showErrorState) {
      return (
        <div className="text-center py-5">
          <div
            className="rounded-3 px-4 py-5 mx-auto border border-danger-subtle bg-danger-subtle"
            style={{ maxWidth: '500px' }}
          >
            <FaExclamationTriangle className="text-danger mb-3 fs-1" />
            <h4 className="text-danger fw-bold mb-3">Erreur de chargement</h4>
            <p className="text-muted mb-4 lead">{error}</p>
            <button className="btn btn-primary rounded-pill px-4 py-2 mb-3" onClick={handleRetry}>
              <FaSyncAlt className="me-2" /> Réessayer
            </button>
          </div>
        </div>
      );
    }

    if (showNoResults) {
      const hasCols = filters.targetMarket === 'Public' || filters.targetMarket === '' || filters.targetMarket === 'Private';
      return (
        <>
          <SecurityAdvancedFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFiltersLocal}
            loading={false}
            isSearching={false}
          />
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <div>
                <h6 className="mb-0 fw-bold">Titres</h6>
                <small className="text-muted">0 résultat</small>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-striped mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3 fw-semibold" style={{ width: '100px' }}>Code</th>
                      <th className="fw-semibold">Nom</th>
                      <th className="fw-semibold">Émetteur</th>
                      <th className="fw-semibold" style={{ width: '80px' }}>Marché</th>
                      <th className="fw-semibold" style={{ width: '100px' }}>Type</th>
                      <th className="fw-semibold" style={{ width: '80px' }}>Coupon</th>
                      {hasCols && (
                        <>
                          <th className="text-center fw-semibold" style={{ width: '90px' }}>Détenteurs</th>
                          <th className="text-center fw-semibold" style={{ width: '100px' }}>Quantité</th>
                        </>
                      )}
                      <th className="text-end fw-semibold" style={{ width: '100px' }}>Prix</th>
                      <th className="text-center fw-semibold pe-3" style={{ width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={hasCols ? 10 : 8} className="text-center py-5">
                        <div className="text-muted py-4">
                          <h6 className="mb-2 fw-bold">
                            {filters.securityName
                              ? `Aucun titre "${filters.securityName}" trouvé`
                              : filters.securityType
                              ? `Aucun ${filters.securityType === 'Bonds' ? 'obligation' : 'action'}`
                              : filters.targetMarket
                              ? `Aucun titre ${filters.targetMarket === 'Public' ? 'coté' : 'non coté'}`
                              : 'Aucun titre trouvé'}
                          </h6>
                          <p className="mb-3">Modifiez vos filtres ou réinitialisez la recherche</p>
                          <button
                            className="btn btn-outline-light btn-sm rounded-pill px-4"
                            onClick={handleResetFiltersLocal}
                          >
                            Réinitialiser les filtres
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      );
    }

    const hasCols =
      filters.targetMarket === 'Public' ||
      filters.targetMarket === '' ||
      filters.targetMarket === 'Private';

    return (
      <>
        <SecurityAdvancedFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFiltersLocal}
          loading={loading}
          isSearching={false}
        />

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
            <div>
              <h6 className="mb-0 fw-bold">Titres</h6>
              <small className="text-muted">
                {pagination.totalCount.toLocaleString()} résultat{pagination.totalCount > 1 ? 's' : ''}
              </small>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3 fw-semibold" style={{ width: '100px' }}>Code</th>
                    <th className="fw-semibold">Nom</th>
                    <th className="fw-semibold">Émetteur</th>
                    <th className="fw-semibold" style={{ width: '80px' }}>Marché</th>
                    <th className="fw-semibold" style={{ width: '100px' }}>Type</th>
                    <th className="fw-semibold" style={{ width: '80px' }}>Coupon</th>
                    {hasCols && (
                      <>
                        <th className="text-center fw-semibold" style={{ width: '90px' }}>Détenteurs</th>
                        <th className="text-center fw-semibold" style={{ width: '100px' }}>Quantité</th>
                      </>
                    )}
                    <th className="text-end fw-semibold" style={{ width: '100px' }}>Prix</th>
                    <th className="text-center fw-semibold pe-3" style={{ width: '60px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {securities.map(security => (
                    <SecurityRowAdvanced
                      key={security.securityId}
                      security={security}
                      role={role}
                      onViewDetails={handleViewDetails}
                      onEditPrice={handleEditPrice}
                      onViewHolders={handleViewHolders}
                      onGenerateReport={handleGenerateReport}
                      onCreateTransaction={handleCreateTransaction}
                      onGenerateDocuments={handleGenerateDocuments}
                      onViewTransactionDetails={handleViewTransactionDetails}
                      onViewStatistics={handleViewStatistics}
                      onDeleteSecurity={handleDeleteSecurity}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {securities.length > 0 && pagination.totalPages > 1 && (
              <div className="border-top">
                <SecurityPagination
                  currentPage={pagination.pageNumber}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="container-fluid px-0">
      {renderContent()}

      <SecurityDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        securityId={selectedSecurityId}
        role={role}
        onDelete={handleDeleteSecurity}
        onEditPrice={handleEditPrice}
      />

      <EditPriceModal
        show={showEditPriceModal}
        onHide={() => setShowEditPriceModal(false)}
        priceData={editPriceData}
        onUpdate={handleUpdatePrice}
        securityName={editPriceData?.securityName || 'Titre inconnu'}
      />

      <SecurityHoldersModal
        show={showHoldersModal}
        onHide={() => setShowHoldersModal(false)}
        securityId={selectedSecurityForHolders?.id}
        securityName={selectedSecurityForHolders?.name}
        targetMarket={selectedSecurityForHolders?.targetMarket}
      />

      <ReportGenerationModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        securityId={selectedSecurityForReport?.id}
        securityName={selectedSecurityForReport?.name}
        mode={reportMode}
      />

      {/* PrivateTransactionModal — onSuccess déclenche le refresh silencieux */}
      <PrivateTransactionModal
        show={showTransactionModal}
        onHide={() => setShowTransactionModal(false)}
        security={selectedSecurityForTransaction}
        onSuccess={handleTransactionSuccess}   // ← nouveau prop
      />

      <ReportGenerationModal
        show={showDocumentsModal}
        onHide={() => setShowDocumentsModal(false)}
        security={selectedSecurityForDocuments}
        mode="private"
      />

      <TransactionDetailsModal
        show={showTransactionDetailsModal}
        onHide={() => setShowTransactionDetailsModal(false)}
        securityId={selectedSecurityForTransactions?.id}
        securityName={selectedSecurityForTransactions?.name}
        domainId={selectedSecurityForTransactions?.domainId}
      />

      <PrivateTransactionsStatisticsModal
        show={showStatisticsModal}
        onClose={() => setShowStatisticsModal(false)}
        securityId={selectedSecurityForStatistics?.id}
        securityName={selectedSecurityForStatistics?.name}
        securitySymbol={selectedSecurityForStatistics?.symbol}
      />
    </div>
  );
};

export default memo(AdvancedSecurities);